import type { Rive } from '@rive-app/react-canvas';
import type { AnalyticsEvents } from '@sunflower-games/analytics-events';

import { analytics } from '../../../../services/analytics';

import { guardRepeatClicks } from './bannerCtaAction';

// The CTA's label as BI logs labels (verbatim, lowercased), when the Rive file doesn't expose `cta_label`.
// Product: the label is always the same; the current banner files draw "PLAY NOW!" in an unnamed text run.
export const MAIN_BANNER_CTA_BUTTON_TEXT = 'play now!';
// A view-model string property, else a named text run, a designer may expose the label as.
export const CTA_LABEL_NAME = 'cta_label';

// BI's lobby_cta_banner spec: its own event_category, never the category banner's 'lobby_banner'.
const MAIN_BANNER_IDENTITY = {
  event_parent: 'lobby',
  event_category: 'main_banner',
  page_url: {},
} as const;

export const getMainBannerImpressionProps = () => ({ ...MAIN_BANNER_IDENTITY, event_action: 'impression' }) as const;

export const getMainBannerCtaClickProps = (buttonText: string) =>
  ({ ...MAIN_BANNER_IDENTITY, event_action: 'click', button_text: buttonText }) as const;

// main_banner_impression and main_banner_cta_click aren't keyed in @sunflower-games/analytics-events yet (its
// cta_click is the category banner's, unchanged); same cast as sendCategoryBannerEvent, kept in this one place.
function sendMainBannerEvent(
  eventName: 'main_banner_impression' | 'main_banner_cta_click',
  params: ReturnType<typeof getMainBannerImpressionProps> | ReturnType<typeof getMainBannerCtaClickProps>,
) {
  return analytics.sendEvent(
    eventName as keyof AnalyticsEvents,
    params as unknown as AnalyticsEvents[keyof AnalyticsEvents],
  );
}

export const sendMainBannerImpression = () =>
  sendMainBannerEvent('main_banner_impression', getMainBannerImpressionProps());

export const sendMainBannerCtaClick = (buttonText: string) =>
  sendMainBannerEvent('main_banner_cta_click', getMainBannerCtaClickProps(buttonText));

const normalizeLabel = (label: unknown) => (typeof label === 'string' ? label.trim().toLowerCase() : '');

const readSafely = (read: () => unknown) => {
  try {
    return normalizeLabel(read());
  } catch {
    return '';
  }
};

/**
 * The CTA label of one Rive instance, for main_banner_cta_click's button_text: its view-model string `cta_label`, else its text
 * run `cta_label`, else MAIN_BANNER_CTA_BUTTON_TEXT; trimmed and lowercased. Never throws.
 * A missing VM property is a silent null, but getTextRunValue console.warns on every miss, so the text run is read
 * once per instance (a label is static) and the VM string, which a file may bind, on every tap.
 */
export function createMainBannerCtaLabel(rive: Pick<Rive, 'viewModelInstance' | 'getTextRunValue'> | null) {
  let textRunLabel: string | undefined;
  return () => {
    if (!rive) return MAIN_BANNER_CTA_BUTTON_TEXT;
    const viewModelLabel = readSafely(() => rive.viewModelInstance?.string(CTA_LABEL_NAME)?.value);
    if (viewModelLabel) return viewModelLabel;
    textRunLabel ??= readSafely(() => rive.getTextRunValue(CTA_LABEL_NAME));
    return textRunLabel || MAIN_BANNER_CTA_BUTTON_TEXT;
  };
}

/**
 * The Rive `user_click` handler: one main_banner_cta_click per tap that runs the CTA, sent before the (possibly async) action.
 * A hidden CTA (null action) and a tap swallowed by the repeat guard send nothing.
 */
export function createMainBannerCtaClick(getAction: () => (() => void) | null, getButtonText: () => string) {
  return guardRepeatClicks(() => {
    const action = getAction();
    if (!action) return;
    void sendMainBannerCtaClick(getButtonText());
    action();
  });
}
